import React from "react";
import { Button } from "@design-system/Button/Button";
import type { DateOnlyRange } from "./DatePicker.types";
import styles from "./DatePicker.module.scss";

interface Props {
    value?: DateOnlyRange;
    minDate?: string;
    maxDate?: string;
    onValueChange: (value: DateOnlyRange) => void;
}

const getCalendarYearRange = (year: number) => ({
    from: `${year}-01-01`,
    to: `${year}-12-31`,
});

const getSelectedCalendarYear = (
    value: DateOnlyRange | undefined,
): number | undefined => {
    if (!value?.from || !value.to) {
        return undefined;
    }
    const year = Number(value.from.slice(0, 4));
    const yearRange = getCalendarYearRange(year);
    return value.from === yearRange.from && value.to === yearRange.to
        ? year
        : undefined;
};

const isYearWithinBounds = (
    year: number,
    minDate?: string,
    maxDate?: string,
): boolean => {
    const range = getCalendarYearRange(year);
    return (
        (!minDate || range.from >= minDate) && (!maxDate || range.to <= maxDate)
    );
};

export const MonthRangeAdjacentYearButtons = ({
    value,
    minDate,
    maxDate,
    onValueChange,
}: Props) => {
    const selectedYear = getSelectedCalendarYear(value);
    if (selectedYear === undefined) {
        return null;
    }

    const previousYear = selectedYear - 1;
    const nextYear = selectedYear + 1;

    return (
        <div className={styles.monthRangeAdjacentYearButtons}>
            <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!isYearWithinBounds(previousYear, minDate, maxDate)}
                onClick={() =>
                    onValueChange(getCalendarYearRange(previousYear))
                }
            >
                Select previous year
            </Button>
            <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!isYearWithinBounds(nextYear, minDate, maxDate)}
                onClick={() => onValueChange(getCalendarYearRange(nextYear))}
            >
                Select next year
            </Button>
        </div>
    );
};
